import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/config";
import { useSite } from "@/lib/content";
import { useSeo } from "@/lib/seo";
import { getLang, t } from "@/lib/i18n";
import { path } from "@/lib/routes";
import { bookingLegal, bookingVisible, errText, fmt, normalizePlate, validEmail, validPhone } from "@/lib/booking";
import PageIntro from "@/components/PageIntro";
import DateStep from "@/components/booking/DateStep";
import CountsStep from "@/components/booking/CountsStep";
import ContactStep from "@/components/booking/ContactStep";
import SummaryStep, { ParkNote } from "@/components/booking/SummaryStep";

const STEP_OF = { date: 0, counts: 1, name: 2, phone: 2, email: 2, plate: 2, accept: 3 };
const INITIAL = { date: "", adult: 1, child: 0, free: 0, name: "", phone: "", email: "", plate: "", accept: false, website: "" };

function validate(step, v, cfg, avail) {
  const e = {};
  const total = v.adult + v.child + v.free;
  if (step >= 0) {
    const d = avail.days.find((x) => x.date === v.date);
    if (!d) e.date = "date";
    else if (d.closed) e.date = "dateClosed";
    else if (d.remaining < total) e.date = "capacity";
  }
  if (step >= 1) {
    if (total < 1) e.counts = "countsMin";
    else if (total > cfg.max_persons) e.counts = "countsMax";
    else if (v.adult < 1) e.counts = "adultMin";
  }
  if (step >= 2) {
    const n = v.name.trim();
    if (n.length < 3 || !n.includes(" ")) e.name = "name";
    if (!validPhone(v.phone)) e.phone = "phone";
    if (!validEmail(v.email)) e.email = "email";
    if (v.plate.trim() && !normalizePlate(v.plate)) e.plate = "plate";
  }
  if (step >= 3 && !v.accept) e.accept = "accept";
  return e;
}

function Wizard({ cfg, avail, legal }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [v, setV] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const heading = useRef(null);
  const moved = useRef(false);
  const set = (patch) => setV((cur) => ({ ...cur, ...patch }));
  const msgs = Object.fromEntries(Object.entries(errors).map(([k, c]) => [k, errText(c, cfg)]));

  useEffect(() => {
    if (moved.current) heading.current?.focus();
    moved.current = true;
  }, [step]);

  const goto = (s) => {
    setErrors({});
    setStatus({ state: "idle", message: "" });
    setStep(s);
  };
  const next = () => {
    const e = Object.fromEntries(Object.entries(validate(step, v, cfg, avail)).filter(([k]) => STEP_OF[k] === step));
    setErrors(e);
    if (Object.keys(e).length) return document.getElementById(step === 2 ? `booking-${Object.keys(e)[0]}` : "booking-step-title")?.focus();
    goto(step + 1);
  };

  const submit = async () => {
    const e = validate(3, v, cfg, avail);
    if (Object.keys(e).length) {
      setErrors(e);
      return setStep(Math.min(...Object.keys(e).map((k) => STEP_OF[k])));
    }
    setStatus({ state: "sending", message: "" });
    try {
      const { data } = await axios.post(`${API}/bookings`, { ...v, lang: getLang() }, { withCredentials: true });
      if (!data.ticket) throw new Error("no ticket");
      navigate(`${path("bilet")}/${data.ticket.token}`, { state: { ticket: data.ticket, fresh: true } });
    } catch (err) {
      const res = err.response;
      const fields = res?.data?.detail?.fields;
      if (fields) {
        setErrors(fields);
        setStep(Math.min(...Object.keys(fields).map((k) => STEP_OF[k] ?? 3)));
      }
      const key = res?.status === 429 ? "rate" : res?.status === 403 ? "closed" : res?.status === 409 ? "capacity" : fields ? null : "generic";
      setStatus({ state: "error", message: key ? t(`bilet.errors.${key}`) : "" });
    }
  };

  const steps = t("bilet.steps");
  const titles = [t("bilet.date.title"), t("bilet.counts.title"), t("bilet.contact.title"), t("bilet.summary.title")];
  const persons = v.adult + v.child + v.free;

  return (
    <section className="safe-x max-w-7xl mx-auto pb-24 min-h-[720px]" data-testid="booking-wizard">
      <ol className="flex gap-2 mb-8" aria-label={t("bilet.progress")}>
        {steps.map((s, i) => (
          <li key={s} className="flex-1" aria-current={i === step ? "step" : undefined}>
            <span className={`block h-1.5 rounded-full ${i <= step ? "bg-forest" : "bg-line"}`} aria-hidden="true" />
            <span className={`mt-2 block text-xs sm:text-sm font-semibold ${i === step ? "text-forest" : "text-ink-muted"}`}>{s}</span>
          </li>
        ))}
      </ol>
      <p className="text-sm text-ink-muted" data-testid="booking-step-indicator">{fmt(t("bilet.stepOf"), { n: step + 1 })}</p>
      <h2 id="booking-step-title" ref={heading} tabIndex={-1} className="font-serif text-3xl sm:text-4xl text-forest mt-1 mb-6 outline-none" data-testid="booking-step-title">{titles[step]}</h2>
      {step === 0 && <DateStep value={v.date} onChange={(date) => set({ date })} avail={avail} persons={persons} error={msgs.date} />}
      {step === 1 && <CountsStep v={v} set={set} cfg={cfg} error={msgs.counts} />}
      {step === 2 && <ContactStep v={v} set={set} errors={msgs} />}
      {step === 3 && <SummaryStep v={v} set={set} cfg={cfg} legal={legal} error={msgs.accept} />}
      {status.state === "error" && status.message && <p role="alert" className="mt-6 rounded-xl bg-[#FEF3F2] px-4 py-3 text-sm font-medium text-[#B42318]" data-testid="booking-error">{status.message}</p>}
      <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3">
        {step > 0 && (
          <button type="button" onClick={() => goto(step - 1)} className="inline-flex items-center justify-center min-h-[52px] px-8 rounded-full border border-forest/30 text-forest font-semibold hover:bg-forest/[0.05]" data-testid="booking-back-button">{t("bilet.back")}</button>
        )}
        {step < 3 ? (
          <button type="button" onClick={next} className="inline-flex items-center justify-center min-h-[52px] px-10 rounded-full bg-forest text-cream font-semibold hover:bg-forest-dark transition-colors" data-testid="booking-next-button">{t("bilet.next")}</button>
        ) : (
          <button type="button" onClick={submit} disabled={status.state === "sending"} className="inline-flex items-center justify-center min-h-[52px] px-10 rounded-full bg-forest text-cream font-semibold hover:bg-forest-dark transition-colors disabled:opacity-60" data-testid="booking-submit-button">
            {status.state === "sending" ? t("bilet.summary.sending") : t("bilet.summary.submit")}
          </button>
        )}
      </div>
    </section>
  );
}

function PreviewBlockers({ blockers }) {
  if (!blockers?.length) return null;
  return (
    <div className="safe-x max-w-7xl mx-auto mb-8" data-testid="booking-preview-blockers">
      <div className="rounded-2xl border border-dashed border-[#C9A86A] bg-[#FFFBEB] p-5 text-[#78350F]">
        <p className="font-semibold">{t("bilet.previewTitle")}</p>
        <p className="text-sm mt-1">{t("bilet.previewText")}</p>
        <ul className="list-disc pl-5 mt-2 text-sm space-y-1">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
      </div>
    </div>
  );
}

export default function Bilet() {
  useSeo("bilet");
  const { site, content, isPreview } = useSite();
  const visible = bookingVisible(site, content, isPreview);
  const [cfg, setCfg] = useState(null);
  const [avail, setAvail] = useState(null);

  useEffect(() => {
    if (!visible) return;
    const opts = { withCredentials: true };
    axios.get(`${API}/tickets/config`, opts).then((r) => setCfg(r.data)).catch(() => setCfg({ live: false }));
    axios.get(`${API}/tickets/availability`, opts).then((r) => setAvail(r.data)).catch(() => setAvail({ capacity: 0, days: [] }));
  }, [visible, isPreview]);

  if (!visible) return <Navigate to={path("ziyaret")} replace />;
  const usable = cfg && (cfg.live || isPreview) && cfg.prices && avail?.days?.length;

  return (
    <div data-testid="bilet-page">
      <PageIntro eyebrow={t("bilet.eyebrow")} title={t("bilet.title")} text={t("bilet.text")} testId="bilet-intro" />
      <div className="safe-x max-w-7xl mx-auto mb-10 grid gap-3 md:grid-cols-2">
        <p className="rounded-xl bg-cream-2 px-4 py-3 text-sm font-semibold text-forest" data-testid="booking-pre-note">{t("bilet.preNote")}</p>
        <ParkNote testId="booking-park-note" />
      </div>
      {isPreview && <PreviewBlockers blockers={cfg?.blockers} />}
      {!cfg || (cfg.live !== false && !avail) ? (
        <p className="safe-x max-w-7xl mx-auto pb-24 min-h-[720px] text-ink-muted" aria-busy="true">{t("bilet.loading")}</p>
      ) : usable ? (
        <Wizard cfg={cfg} avail={avail} legal={bookingLegal(content, isPreview)} />
      ) : (
        <div className="safe-x max-w-7xl mx-auto pb-24 space-y-4" data-testid="booking-closed">
          <h2 className="font-serif text-3xl text-forest">{t("bilet.closedTitle")}</h2>
          <p className="text-ink-muted">{t("bilet.closedText")}</p>
          <Link to={path("ziyaret")} className="inline-flex items-center min-h-[52px] px-7 rounded-full bg-forest text-cream font-semibold">{t("nav.ziyaretInfo")}</Link>
        </div>
      )}
    </div>
  );
}
